import React from 'react';
import './ReportListElement.css';

import TypeTag from '../tags/TypeTag.js';
import { primaryTypeTagNames, secondaryTypeTagNames, tertiaryTypeTagNames, auxiliaryTypeTagNames, otherTagNames } from '../../../models/tag_representations';

const ReportListElement = ({ reportId, reportNumber, incidentNumber, numberOfIssues, numberOfSuggestions, reportType, incidentTime, read, finished, narrativeSummarized, failureMode, result, verbose = false, onButtonClick }) => {
    
    const handleButtonClick = () => {
        onButtonClick(reportId);
    };
    
    // break up reportType, a string of values concatonated by commas, into an array
    const reportTypes = reportType.split(',');
    
    // Order report types by tier (primary, secondary, other) and filter out tertiary types
    const orderedReportTypes = reportTypes
        .filter(type => !auxiliaryTypeTagNames.includes(type))
        .sort((a, b) => {
            const getTierIndex = (type) => {
                if (primaryTypeTagNames.includes(type)) return 0;
                if (secondaryTypeTagNames.includes(type)) return 1;
                if (tertiaryTypeTagNames.includes(type)) return 2;
                if (otherTagNames.includes(type)) return 3;
                return 3; // fallback for any unknown types
            };
            return getTierIndex(a) - getTierIndex(b);
        });

    // Helper function to convert date string to ISO format
    const datetimeToIso = (dateString) => {
        // if datestring is undefined or null, return null
        if (dateString === undefined || dateString === null) {
            return null;
        }

        // convert the date string to a valid ISO 8601 date string and add Z to the end to indicate UTC time
        let isoString = dateString.replace(' ', 'T');
        isoString = isoString + 'Z';
        
        return isoString;
    };

    const convertToLocaleString = (dateString) => {
        const options = { 
            hour: 'numeric', 
            minute: 'numeric', 
            hour12: true, 
            year: 'numeric', 
            month: 'numeric', 
            day: 'numeric'
        };
    
        // if datestring is undefined or null, return 'no data'
        if (dateString === undefined || dateString === null) {
            return '';
        }

        // Print out unconverted date
        // console.log('original dateString:', dateString);
    
        // Use the helper function to convert to ISO format
        const isoString = datetimeToIso(dateString);
        
        if (isoString === null) {
            return '';
        }

        // now convert the ISO string to a date object
        const date = new Date(isoString);
        
        // Print new date
        // console.log('new date:', date); // this is printing out GMT as CDT
    
        return date.toLocaleString([], options);
    };

    // Helper function to convert result value to readable string
    const getResultText = (resultValue) => {
        if (resultValue === 1) return "Positive";
        if (resultValue === 0) return "Negative";
        return "Not Applicable";
    };

    // Determine the appropriate CSS class based on read and finished states
    const getElementClass = () => {
        if (finished) return "list-element-finished";
        if (read) return "list-element-read";
        return "list-element";
    };

    if (verbose) {
        return (
        <div className={getElementClass()} style={{flexDirection: 'column'}}>
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%'}}>
                <div className='medium-div'>
                    {incidentNumber === null ? ( // if incidentNumber is null, display reportNumber
                        <div className="report-number">{reportNumber}</div>
                    ) : (
                        <div className="report-number">{incidentNumber}</div>
                    )}
                </div>
                <div className='tiny-div'>
                    <div className="number-of-issues">{numberOfIssues}</div>
                </div>
                <div className='tiny-div'>
                    <div className="number-of-suggestions">{numberOfSuggestions}</div>
                </div>
                <div className='large-div'>
                {orderedReportTypes.map((type, index) => (
                    <TypeTag key={type} reportType={type} isButton={false}/>
                ))}
                </div>
                <div className='medium-div'>
                    <div className="incident-time">{convertToLocaleString(incidentTime)}</div>
                </div>
                <button className="enter-button" onClick={handleButtonClick}>View Report</button>
            </div>
            {narrativeSummarized && (
                <div className='large-div' style={{marginTop: '10px', textAlign: 'left', width: '100%'}}>
                    <div className="extra-info-div" style={{textAlign: 'left', width: '100%'}}><strong>Summary:</strong> {narrativeSummarized}</div>
                </div>
            )}
            {((result !== null && result !== undefined) || failureMode) && (
                <>
                    <div className='large-div' style={{marginTop: '10px', textAlign: 'left', width: '100%'}}>
                        <div className="extra-info-div" style={{textAlign: 'left', width: '100%'}}><strong>Test Result:</strong> {getResultText(result)}</div>
                    </div>
                    {failureMode && (
                        <div className="large-div" style={{marginTop: '10px', textAlign: 'left', width: '100%'}}>
                            <div className="extra-info-div" style={{textAlign: 'left', width: '100%'}}><strong>Reason:</strong> {failureMode}</div>
                        </div>
                    )}
                </>
            )}
        </div>
        );
    }

    return (
        <div className={getElementClass()}>
            <div className='medium-div'>
                {incidentNumber === null ? ( // if incidentNumber is null, display reportNumber
                    <div className="report-number">{reportNumber}</div>
                ) : (
                    <div className="report-number">{incidentNumber}</div>
                )}
            </div>
            <div className='tiny-div'>
                <div className="number-of-issues">{numberOfIssues}</div>
            </div>
            <div className='tiny-div'>
                <div className="number-of-suggestions">{numberOfSuggestions}</div>
            </div>
            <div className='large-div'>
            {orderedReportTypes.map((type, index) => (
                <TypeTag key={type} reportType={type} isButton={false}/>
            ))}
            </div>
            <div className='medium-div'>
                <div className="incident-time">{convertToLocaleString(incidentTime)}</div>
            </div>
            <button className="enter-button" onClick={handleButtonClick}>View Report</button>
        </div>
    );
};

export default ReportListElement;